/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { AnvilorFormsApp } from "../store/anvilor-forms-app-store.service";
import { AnvilorNotificationsService } from "@anvilor/ngx-apps-core";
import { combineLatest, Observable, switchMap } from "rxjs";
import { filter } from "rxjs";
import { tap } from "rxjs";
import { map } from "rxjs/operators";
import { MatDialogRef } from "@angular/material/dialog";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  providers: AnvilorFormsApp.providers(),
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnInit {
  /**
   * Post-submit state for v1 configs. Legacy (v0) configs render their own
   * post-submit content via the `anvilor_forms:form_states` control; v1 configs
   * have no scaffolding controls (yet - see the renderer-library plan), so the
   * shell swaps in the standard thank-you state after an accepted submission.
   */
  showV1PostSubmit$!: Observable<boolean>;

  /**
   * Whether the loaded form is a v1 (`rui_config`) one. v1 configs have no
   * scaffolding controls, so the shell also provides their containing layout
   * (see `.rui-container.v1-layout`) — legacy configs lay themselves out via
   * `anvilor_forms:form_layout` and must not be constrained here.
   */
  isV1Config$!: Observable<boolean>;

  constructor(
    public app: AnvilorFormsApp,
    private notify: AnvilorNotificationsService,
  ) {
    this.app.initialize();
    this.isV1Config$ = this.app.store.select(
      (state) => !!state.anvilor_form_config?.display_config?.rui_config,
    );
    this.showV1PostSubmit$ = combineLatest([
      this.app.submit.submissionAccepted$,
      this.isV1Config$,
    ]).pipe(map(([accepted, isV1]) => accepted && isV1));
  }

  ngOnInit(): void {
    this.checkIfStuckWaitingForEditConfig();
  }

  /**
   * Helper to open a simple dialog/notification if we can figure out that we are
   * waiting on edit config to come back. Rarely would need this...
   */
  private checkIfStuckWaitingForEditConfig() {
    this.app.subscribeTo(
      this.app.loading.waitingForEditConfig$.pipe(
        map(() => this.loadingEditConfigDialog()),
        switchMap((dialog) =>
          this.app.loading.editConfigLoaded$.pipe(
            filter((loaded) => loaded),
            tap(() => dialog.close()),
          ),
        ),
      ),
    );
  }

  private loadingEditConfigDialog(): MatDialogRef<any> {
    return this.notify.dialog.open({
      disableClose: true,
      data: { title: "Loading", actionButtons: [], body: "Loading edit config..." },
    });
  }
}
